import React, { useEffect, useState, useContext } from "react";
import { useImageValidation } from "../../../../customHooks/useImageValidation";
import FacilitiesForm from "../FacilitiesForm/FacilitiesForm";
import { useNavigate, useLocation } from "react-router-dom";
import importedMunicipalities from "../../../../assets/municipalities.json";
import axios from "axios";
import AdPreviewContext from "../../../../context/AdPreviewContext";
import { v4 as uuidv4 } from "uuid";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import importedRoomTypes from "../../../../assets/roomTypes.json";
import Loading from "../Loading/Loading";
import { backend } from "../../../../backend";
import { logAxiosError } from "../../../../logging/error";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import { useCallbackPrompt } from "../../../../context/useCallbackPrompt";
import { useAuth } from "../../../../context/AuthContext";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "./createAd.css";
import { Helmet } from "react-helmet-async";
import {
  authenticateForm,
  hasValidPrice,
  requiredFieldsCheck,
} from "../../utilities/formAuth";
import { useAutoExpand } from "../../../../customHooks/useAutoExpand";
import RoundedButton from "../../../../components/Buttons/RoundedButton/RoundedButton";

const { municipalities } = importedMunicipalities;
const { roomTypes } = importedRoomTypes;
const defaultFormData = {
  name: "",
  owner_name: "",
  address: "",
  city: "",
  municipality: "",
  zip_code: "",
  type_of_room: "",
  area: "",
  capacity: "",
  workspaces: "",
  facilities: [],
  description: "",
  images: [],
  fully_vacant: true,
  hourly_rent: "",
  daily_rent: "",
  monthly_rent: "",
  workspace_rent: "",
  vat_tax_required: false,
  contract_type: "",
};

function CreateAd(props) {
  const [formData, setFormData] = useState(defaultFormData);
  const [newImages, setNewImages] = useState([]);
  const [newAdId, setNewAdId] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  // const [attemptToSubmit, setAttemptToSubmit] = useState(false);
  const [viewPreview, setViewPreview] = useState(false);
  const [showPage, setShowPage] = useState(false);
  const [showSaveAdModal, setShowSaveAdModal] = useState(false);
  const [showCreateAdModal, setShowCreateAdModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const { adPreviewInfo, setAdPreviewInfo } = useContext(AdPreviewContext);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ImageTooBig, setImageTooBig] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const {
    showPrompt: showWarningModal,
    confirmNavigation,
    cancelNavigation,
  } = useCallbackPrompt(
    showPrompt,
    "Du har osparade ändringar. Är du säker på att du vill lämna sidan?",
  );
  const { validateFiles, checkMagicBytes, clearValidationErrors } =
    useImageValidation();

  const autoExpand = useAutoExpand();

  useEffect(() => {
    const { info, images, hasChanged } = adPreviewInfo;
    if (hasChanged) {
      setShowPrompt(true);
    }
    if (location.state?.from === "preview") {
      setFormData(info);
      setNewImages(images);
      setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
    }
    setShowPage(true);

    // eslint-disable-next-line
  }, [location]);

  useEffect(() => {
    setDisableSubmit(!requiredFieldsCheck(formData, newImages));
  }, [formData, newImages]);

  const createAdHandler = () => {
    // setAttemptToSubmit(true);

    const isValid = authenticateForm(formData, newImages, setInvalidFields);
    if (isValid) {
      setLoading(true);
      createAdRequest();
    }
  };

  const createAdRequest = async () => {
    try {
      const { data } = await axios.post(backend("/api/listings/create"), {
        token: localStorage.getItem("token"),
        data: {
          ...formData,
          images: newImages,
        },
      });

      if (data.status) {
        setNewAdId(data.ad._id);
        uploadImages(data.ad._id, setShowCreateAdModal);
      }
    } catch (error) {
      logAxiosError(error);
      setLoading(false);
      setErrorMessage(error.response?.data?.msg || "");
      setShowErrorModal(true);
      if (error.response?.status === 401) {
        auth.logout();
      }
    }
  };

  const saveAdHandler = () => {
    const isValid = authenticateForm(formData, newImages, setInvalidFields);

    if (isValid) {
      setLoading(true);
      saveAdRequest();
    }
  };

  const saveAdRequest = async () => {
    try {
      const { data } = await axios.post(backend("/api/listings/create"), {
        token: localStorage.getItem("token"),
        data: {
          ...formData,
          images: newImages,
          active: false,
        },
      });

      if (data.status) {
        uploadImages(data.ad._id, setShowSaveAdModal);
      }
    } catch (error) {
      logAxiosError(error);
      setLoading(false);
      setErrorMessage(error.response?.data?.msg || "");
      setShowErrorModal(true);
      if (error.response?.status === 401) {
        auth.logout();
      }
    }
  };

  const uploadImages = async (ad_id, setShowModal) => {
    let errorOccured = false;

    for (let image of newImages) {
      try {
        // Validera filtyp innan uppladdning (defense-in-depth)
        const isValidImage = await checkMagicBytes(image.file);
        if (!isValidImage) {
          console.error(
            "Ogiltig filtyp blockerad vid uppladdning:",
            image.file.name,
          );
          errorOccured = true;
          continue;
        }

        const imageData = new FormData();
        imageData.append("file", image.file);
        imageData.append("upload_preset", "oi2aoqjs");
        imageData.append("public_id", `/${ad_id}/${image.id}`);

        const response = await axios.post(
          "https://api.cloudinary.com/v1_1/vakansa/image/upload",
          imageData,
        );
        if (response.status !== 200) {
          errorOccured = true;
        }
      } catch (error) {
        console.error("Bilduppladdning misslyckades för:", image.id, error);
        errorOccured = true;
      }
    }
    setLoading(false);
    setErrorMessage(
      errorOccured
        ? "Ett problem uppstod under bild uppladdningen. Det kan ha hänt att inte alla bilder blev uppladdade."
        : "",
    );
    if (!errorOccured) {
      setShowPrompt(false);
      setShowModal(true);
      setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
    } else {
      setShowPrompt(true);
      setShowErrorModal(true);
    }
  };

  const formHasChanged = () => {
    setAdPreviewInfo((prev) => ({ ...prev, hasChanged: true }));
    setShowPrompt(true);
  };

  const onChangeHandler = (e) => {
    const isNumericInput = e.target.inputMode === "numeric";
    const isNumericValue = new RegExp(/^[0-9]*$/).test(e.target.value);
    if (isNumericInput && !isNumericValue) return;
    formHasChanged();
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const onImagesChange = async (e) => {
    const files = e.target.files;

    if (files.length < 1 || files.length > 12) return;
    if (newImages.length + files.length > 12) {
      setImageTooBig(
        "Du kan inte ladda upp fler än 12 bilder. Du har nått maximum",
      );
      return;
    }

    // Rensa tidigare valideringsfel
    clearValidationErrors();

    // Använd hook för validering
    const { rejectedImages, errors } = await validateFiles([...files]);

    // Skapa objekt från giltiga filer
    const validFiles = [...files].filter(
      (file) => !rejectedImages.includes(file),
    );
    const newImageObjects = validFiles
      .map((file) => {
        // Check för duplikat
        const existingImage = newImages.find(
          (image) => image.file.name === file.name,
        );
        if (existingImage) {
          setImageTooBig(`Image "${file.name}" already exists in the list`);
          return null;
        }
        return {
          url: URL.createObjectURL(file),
          id: uuidv4(),
          file,
        };
      })
      .filter(Boolean); // Filtrera bort null värden

    // Hantera felmeddelanden med samma singular/plural logik som EditAd
    // Hantera felmeddelanden
    if (rejectedImages.length > 0) {
      const errorMsg =
        errors.length > 0
          ? errors.join(", ")
          : rejectedImages.length === 1
            ? `1 bild avvisades`
            : `${rejectedImages.length} bilder avvisades`;
      setImageTooBig(errorMsg);
    } else {
      setImageTooBig(null);
    }

    formHasChanged();
    setNewImages((prevData) => [...prevData, ...newImageObjects]);
  };

  const removeImageHandler = (image) => {
    formHasChanged();
    setNewImages((prevData) =>
      prevData.filter((currentImage) => currentImage.id !== image),
    );
  };

  useEffect(() => {
    if (!viewPreview && !showPrompt) return;
    navigate("/mina-sidor/skapa-annons/forhandsvy", {
      state: { prevRoute: location.pathname },
    });
    // eslint-disable-next-line
  }, [viewPreview]);

  const adPreviewHandler = () => {
    setShowPrompt(false);
    setAdPreviewInfo((prev) => ({
      ...prev,
      info: formData,
      images: newImages,
    }));
    setViewPreview(true);
  };

  const validateField = (e) => {
    const { value, name } = e.target;

    const defaultDropdownValues = ["Välj kommun", "Typ av lokal"];
    const invalidDropdownValue = defaultDropdownValues.includes(value);

    if (
      !value ||
      invalidDropdownValue ||
      (name === "zip_code" && !/^\d{5}$/.test(value))
    ) {
      setInvalidFields([...invalidFields, name]);
    } else {
      setInvalidFields(invalidFields.filter((field) => field !== name));
    }

    //special case check for the price group
    if (
      ["hourly_rent", "daily_rent", "monthly_rent", "workspace_rent"].includes(
        name,
      )
    ) {
      if (hasValidPrice(formData)) {
        setInvalidFields(invalidFields.filter((field) => field !== "rent"));
      } else {
        setInvalidFields([
          ...invalidFields.filter((field) => field !== "rent"),
          "rent",
        ]);
      }
    }
  };

  const tooltipText = {
    moms: "Momsad: Moms läggs på hyran<br/>Icke momsad: Lokalen hyrs ut utan moms",
  };

  const renderTooltipIcon = (tooltipHtml) => (
    <svg
      aria-hidden="true"
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltipHtml}
      viewBox="0 0 512 512"
      fill="currentColor"
      height="24px"
      width="24px"
      {...props}
    >
      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0 0 114.6 0 256s114.6 256 256 256zm-86.2-346.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1 0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6-13.3 0-24-10.7-24-24v-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1 0-8.4-6.8-15.1-15.1-15.1h-58.3c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM288 352c0 17.7-14.3 32-32 32s-32-14.3-32-32 14.3-32 32-32 32 14.3 32 32z" />
    </svg>
  );

  return (
    showPage && (
      <>
        <Helmet>
          <title>Skapa annons | Vakansa</title>
          <meta
            name="description"
            content="Skapa och publicera din annons enkelt på Vakansa för att nå potentiella hyresgäster!"
          />
          <link rel="canonical" href={backend("/mina-sidor/skapa-annons")} />

          <meta property="og:image" content="/images/logo/meta-logo.jpg" />
          <meta property="og:title" content="Skapa en annons på Vakansa" />
          <meta
            property="og:description"
            content="Skapa och publicera din annons enkelt på Vakansa för att nå potentiella hyresgäster!"
          />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="create-ad-container">
          <span className="create-ad-inline-msg">
            (Fält markerade med * är obligatoriska och måste fyllas i)
          </span>
          <br />
          <br />
          <br />
          <form>
            <div className="create-ad-input-row">
              <div className="create-ad-input-col create-ad-input-col-lg">
                <label htmlFor="createAdOwner">
                  <div>
                    Fastighetsägare*{" "}
                    <span className="create-ad-information-italics">
                      (Detta fält publiceras inte i annonsen)
                    </span>
                  </div>
                  <input
                    data-testid="input-create-ad-owner-name"
                    className="create-ad-input-fastighet"
                    type="text"
                    id="createAdOwner"
                    name="owner_name"
                    placeholder="Fastighetsägare"
                    value={formData?.owner_name}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                    aria-invalid={
                      invalidFields.includes("owner_name") ? "true" : "false"
                    }
                    aria-describedby={
                      invalidFields.includes("owner_name")
                        ? "owner-error"
                        : undefined
                    }
                  />
                  {invalidFields.includes("owner_name") && (
                    <span
                      className="create-ad-error-msg"
                      id="owner-error"
                      role="alert"
                    >
                      Ange en giltig fastighetsägare{" "}
                    </span>
                  )}
                </label>
              </div>
            </div>
            <div className="create-ad-input-row">
              <div className="create-ad-input-col create-ad-input-col-lg">
                <label htmlFor="createAdAddress">
                  Adress*
                  <input
                    data-testid="input-create-ad-address"
                    type="text"
                    id="createAdAddress"
                    name="address"
                    placeholder="Adress"
                    value={formData?.address}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                    aria-invalid={
                      invalidFields.includes("address") ? "true" : "false"
                    }
                    aria-describedby={
                      invalidFields.includes("address")
                        ? "address-error"
                        : undefined
                    }
                  />
                  {invalidFields.includes("address") && (
                    <span
                      className="create-ad-error-msg"
                      id="address-error"
                      role="alert"
                    >
                      Ange en giltig adress{" "}
                    </span>
                  )}
                </label>
              </div>
              <div className="create-ad-input-col create-ad-input-col-md">
                <label htmlFor="createAdCity">
                  Stad*
                  <input
                    data-testid="input-create-ad-city"
                    type="text"
                    id="createAdCity"
                    name="city"
                    placeholder="Stad"
                    value={formData?.city}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                    aria-invalid={
                      invalidFields.includes("city") ? "true" : "false"
                    }
                    aria-describedby={
                      invalidFields.includes("city") ? "city-error" : undefined
                    }
                  />
                  {invalidFields.includes("city") && (
                    <span
                      className="create-ad-error-msg"
                      id="city-error"
                      role="alert"
                    >
                      Ange stad{" "}
                    </span>
                  )}
                </label>
              </div>
              <div className="create-ad-input-col create-ad-input-col-md">
                <label htmlFor="createAdMunicipality">
                  Kommun*
                  <div className="create-ad-select-wrapper">
                    <select
                      data-testid="input-create-ad-municipality"
                      id="createAdMunicipality"
                      name="municipality"
                      defaultValue="Välj kommun"
                      onChange={onChangeHandler}
                      onBlur={validateField}
                      aria-invalid={
                        invalidFields.includes("municipality")
                          ? "true"
                          : "false"
                      }
                      aria-describedby={
                        invalidFields.includes("municipality")
                          ? "municipality-error"
                          : undefined
                      }
                    >
                      <option hidden>Välj kommun</option>
                      <option>Stockholm </option>
                      <option>Göteborg </option>
                      <option>Malmö </option>
                      <option>Uppsala</option>
                      <optgroup label="Alla kommuner">
                        {municipalities.map((item, i) => (
                          <option key={i} value={item}>
                            {item}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    <img
                      src="/images/icons/arrow-down.svg"
                      alt="arrow pointing downwards"
                      aria-hidden="true"
                    />
                  </div>
                  {invalidFields.includes("municipality") && (
                    <span
                      className="create-ad-error-msg"
                      id="municipality-error"
                      role="alert"
                    >
                      Ange kommun{" "}
                    </span>
                  )}
                </label>
              </div>
              <div className="create-ad-input-col create-ad-input-col-sm">
                <label htmlFor="createAdZip">
                  Postkod*
                  <input
                    data-testid="input-create-ad-zipcode"
                    type="text"
                    inputMode="numeric"
                    id="createAdZip"
                    name="zip_code"
                    placeholder="Postkod"
                    value={formData?.zip_code}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                    aria-invalid={
                      invalidFields.includes("zip_code") ? "true" : "false"
                    }
                    aria-describedby={
                      invalidFields.includes("zip_code")
                        ? "zip-code-error"
                        : undefined
                    }
                  />
                  {invalidFields.includes("zip_code") && (
                    <span
                      className="create-ad-error-msg"
                      id="zip-code-error"
                      role="alert"
                    >
                      Ange en giltig postkod (5 siffror){" "}
                    </span>
                  )}
                </label>
              </div>
            </div>
            <div className="create-ad-input-row">
              <div className="create-ad-input-col create-ad-input-col-md">
                <label htmlFor="createAdType">
                  Lokal*
                  <div className="create-ad-select-wrapper create-ad-input-col-md">
                    <select
                      data-testid="input-create-ad-roomtype"
                      id="createAdType"
                      name="type_of_room"
                      defaultValue="Typ av lokal"
                      onChange={onChangeHandler}
                      onBlur={validateField}
                      aria-invalid={
                        invalidFields.includes("type_of_room")
                          ? "true"
                          : "false"
                      }
                      aria-describedby={
                        invalidFields.includes("type_of_room")
                          ? "type-of-room-error"
                          : undefined
                      }
                    >
                      <option hidden>Typ av lokal</option>
                      {roomTypes.map((roomType, i) => (
                        <option key={i}>{roomType}</option>
                      ))}
                    </select>
                    <img
                      src="/images/icons/arrow-down.svg"
                      alt="arrow pointing downwards"
                      aria-hidden="true"
                    />
                  </div>
                  {invalidFields.includes("type_of_room") && (
                    <span
                      className="create-ad-error-msg"
                      id="type-of-room-error"
                      role="alert"
                    >
                      Ange lokaltyp{" "}
                    </span>
                  )}
                </label>
              </div>
              <div className="create-ad-input-col create-ad-input-col-sm">
                <label htmlFor="createAdArea">
                  Yta*
                  <input
                    data-testid="input-create-ad-area"
                    id="createAdArea"
                    name="area"
                    type="text"
                    inputMode="numeric"
                    placeholder="kvm"
                    value={formData?.area}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                    aria-invalid={
                      invalidFields.includes("area") ? "true" : "false"
                    }
                    aria-describedby={
                      invalidFields.includes("area") ? "area-error" : undefined
                    }
                  />
                  {invalidFields.includes("area") && (
                    <span
                      className="create-ad-error-msg"
                      id="area-error"
                      role="alert"
                    >
                      Ange yta{" "}
                    </span>
                  )}
                </label>
              </div>
              <div className="create-ad-input-col create-ad-input-col-md">
                <label htmlFor="createAdMaxPeople">
                  Lämpligt för antal personer
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="createAdMaxPeople"
                  name="capacity"
                  placeholder="st"
                  value={formData?.capacity}
                  onChange={onChangeHandler}
                />
              </div>
              <div className="create-ad-input-col create-ad-input-col-md">
                <label htmlFor="createAdWorkspaces">Antal sittplatser</label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="createAdWorkspaces"
                  name="workspaces"
                  placeholder="st"
                  value={formData?.workspaces}
                  onChange={onChangeHandler}
                />
              </div>
            </div>
            <div className="create-ad-input-row">
              <div className="create-ad-input-col create-ad-input-col-xl">
                <label htmlFor="createAdContractType">Kontraktstyp</label>
                <div className="create-ad-select-wrapper">
                  <select
                    id="createAdContractType"
                    className="contract-type-select"
                    name="contract_type"
                    value={formData?.contract_type}
                    onChange={onChangeHandler}
                  >
                    <option value="">Välj kontraktstyp</option>
                    <option value="Förstahandskontrakt">
                      Förstahandskontrakt
                    </option>
                    <option value="Andrahandskontrakt">
                      Andrahandskontrakt
                    </option>
                  </select>
                  <img
                    src="/images/icons/arrow-down.svg"
                    alt="arrow pointing downwards"
                    aria-hidden="true"
                  />
                </div>
              </div>
              <div className="create-ad-input-col create-ad-input-col-md">
                <div className="create-ad-vat-tax-wrapper">
                  <fieldset className="radio-group">
                    <legend>
                      Hyrs lokalen ut med moms eller utan moms?{" "}
                      {renderTooltipIcon(tooltipText.moms)}
                    </legend>
                    <span id="moms-info" className="visually-hidden">
                      {tooltipText.moms}
                    </span>
                    <div>
                      <label className="radio-label">
                        <input
                          type="radio"
                          value="true"
                          className="radio-button"
                          checked={formData?.vat_tax_required === true}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              vat_tax_required: true,
                            }))
                          }
                        />
                        &nbsp; Momsad
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          value="false"
                          className="radio-button"
                          checked={formData?.vat_tax_required === false}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              vat_tax_required: false,
                            }))
                          }
                        />
                        &nbsp; Icke momsad
                      </label>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <FacilitiesForm formData={formData} setFormData={setFormData} />
            <div className="create-ad-input-row">
              <div className="create-ad-input-col create-ad-input-col-xxl">
                <h5 htmlFor="createAdDescription">Beskrivning av lokalen*</h5>
                <p>
                  Lyft här information som inte inkluderas här ovan, tex hur man
                  tar sig till lokalen, vad som gör den unik och vilka
                  aktiviteter som inte kan göras här.
                </p>
                <p>
                  <i>
                    Vänligen ange på ett ungefär vilka dagar och tider lokalen
                    ska hyras ut, eller om den är helt vakant.
                  </i>
                </p>
                <p>
                  <strong>OBS! </strong>Det är inte tillåtet att uppge
                  kontaktuppgifter eller andra ledande uppgifter i
                  beskrivningstexten som skulle göra att ni kringgår Vakansas
                  tjänster och våra{" "}
                  <a target="_blank" href="/anvandarvillkor">
                    användarvillkor
                  </a>
                </p>
                <textarea
                  data-testid="input-create-ad-description"
                  id="createAdDescription"
                  name="description"
                  className="form-control"
                  placeholder="Skriv här..."
                  aria-label="Skriv din beskrivning av lokalen här"
                  value={formData?.description}
                  onChange={onChangeHandler}
                  onBlur={validateField}
                  onInput={autoExpand}
                  aria-invalid={
                    invalidFields.includes("description") ? "true" : "false"
                  }
                  aria-describedby={
                    invalidFields.includes("description")
                      ? "description-error"
                      : undefined
                  }
                />
                {invalidFields.includes("description") && (
                  <span
                    className="create-ad-error-msg"
                    id="description-error"
                    role="alert"
                  >
                    Ange beskrivning{" "}
                  </span>
                )}
              </div>
            </div>
            <div>
              <h5>Lägg till bilder*</h5>
              <p>
                Ladda upp minst <strong>4 bilder</strong> för att ge en bra
                presentation av lokalen. Du kan bifoga upp till{" "}
                <strong>12 bilder</strong>.
              </p>
              <p>
                För bästa resultat rekommenderar vi att du inkluderar en
                planritning av lokalen.
              </p>
              <p>
                <b>Maximal filstorlek per bild:</b> 10 MB. Vi stödjer följande
                format: <strong>JPG, PNG, WEBP och GIF</strong>.
              </p>
              <p className="create-ad-image-italics">
                Den första bilden du laddar upp blir annonsens huvudbild.{" "}
                <span>
                  Ordningen på bilderna kan du senare ändra i redigera annons
                  efter att du skapat annonsen
                </span>
              </p>

              {ImageTooBig !== null && (
                <h3
                  style={{
                    color: "red",
                    fontSize: "medium",
                    marginBottom: "1rem",
                  }}
                >
                  {ImageTooBig}
                </h3>
              )}
              <div className="create-ad-image-container">
                {newImages &&
                  newImages.map((image) => (
                    <div key={image.id} className="create-ad-image">
                      <img src={image.url} alt="" />
                      <button
                        type="button"
                        onClick={() => removeImageHandler(image.id)}
                        className="create-ad-image-delete-button"
                        aria-label="Ta bort bild"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          height="2.2em"
                          width="1.6em"
                          {...props}
                        >
                          <path d="M13.41 12l4.3-4.29a1 1 0 10-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 00-1.42 1.42l4.3 4.29-4.3 4.29a1 1 0 000 1.42 1 1 0 001.42 0l4.29-4.3 4.29 4.3a1 1 0 001.42 0 1 1 0 000-1.42z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                <input
                  data-testid="input-create-ad-image"
                  type="file"
                  name="images"
                  id="create-ad-image-input"
                  multiple
                  accept="image/*"
                  onChange={onImagesChange}
                  aria-invalid={
                    invalidFields.includes("images") ? "true" : "false"
                  }
                  aria-describedby={
                    invalidFields.includes("images")
                      ? "images-error"
                      : undefined
                  }
                />
                <label
                  className="create-ad-custom-file-input"
                  htmlFor="create-ad-image-input"
                >
                  <img
                    src="/images/icons/plus-circle.svg"
                    alt="Plus ikon"
                    aria-hidden="true"
                  />
                  <span>Välj bild</span>
                </label>
              </div>
              {invalidFields.includes("images") && (
                <span
                  className="create-ad-error-msg"
                  id="images-error"
                  role="alert"
                >
                  Minst 4 bilder krävs{" "}
                </span>
              )}
            </div>

            <div className="price-section">
              <h5>Ange pris*</h5>
              <p>(fyll i minst 1 fält)</p>
              <div className="create-ad-input-price-row">
                <div className="create-ad-input-col create-ad-input-col-md">
                  <label htmlFor="createAdMinPrice">
                    Timpris (enstaka timmar)
                  </label>
                  <input
                    data-testid="input-create-ad-timePerHour"
                    type="text"
                    inputMode="numeric"
                    name="hourly_rent"
                    id="createAdMinPrice"
                    placeholder="kr/timme"
                    value={formData?.hourly_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
                <div className="create-ad-input-col create-ad-input-col-md">
                  <label htmlFor="createAdMaxPrice">
                    Dagspris (8 timmar i rad)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="createAdMaxPrice"
                    name="daily_rent"
                    placeholder="kr/dag"
                    value={formData?.daily_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
                <div className="create-ad-input-col create-ad-input-col-md">
                  <label htmlFor="createAdMaxPrice">
                    Månadspris (30 dagar i rad)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="createAdMaxPrice"
                    name="monthly_rent"
                    placeholder="kr/månad"
                    value={formData?.monthly_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                  />
                </div>
                <div className="create-ad-input-col create-ad-input-col-md">
                  <label htmlFor="createAdMaxPrice">
                    Pris per arbetsplats (per månad)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="createAdMaxPrice"
                    name="workspace_rent"
                    placeholder="kr/arbetsplats"
                    value={formData?.workspace_rent}
                    onChange={onChangeHandler}
                    onBlur={validateField}
                    aria-invalid={
                      invalidFields.includes("rent") ? "true" : "false"
                    }
                    aria-describedby={
                      invalidFields.includes("rent") ? "rent-error" : undefined
                    }
                  />
                </div>
              </div>
              {invalidFields.includes("rent") && (
                <span
                  className="create-ad-error-msg"
                  id="rent-error"
                  role="alert"
                >
                  Alla ifyllda prisfält måste vara större än 0 kr{" "}
                </span>
              )}
            </div>
            <div className="create-ad-form-customer-info">
              <p>
                Kom ihåg att Vakansa tar 15% av hyresintäkterna vid lyckad
                uthyrning av er lokal. Läs mer om vår prismodell {""}
                <a target="_blank" rel="noreferrer" href="/hyr-ut-lokal">
                  här.
                </a>
              </p>
            </div>
            <div className="create-ad-form-buttons">
              {disableSubmit && (
                <p className="create-ad-form-buttons-disabled-info">
                  *Alla obligatoriska fält måste vara ifyllda för att kunna gå
                  vidare. Scrolla upp för mer information.
                </p>
              )}

              <div className="create-ad-form-buttons-row">
                <RoundedButton
                  color="white"
                  disabled={disableSubmit}
                  type="button"
                  onClick={adPreviewHandler}
                  label="Förhandsvy"
                  size="lg"
                />

                <RoundedButton
                  color="white"
                  disabled={disableSubmit}
                  type="button"
                  onClick={saveAdHandler}
                  label="Spara som utkast"
                  size="lg"
                />

                <RoundedButton
                  dataTestID="input-create-ad-create-btn"
                  color="green"
                  type="button"
                  onClick={createAdHandler}
                  label="Skapa och publicera"
                  size="lg"
                />
              </div>
            </div>
          </form>
          <ReactTooltip className="tooltip" id="my-tooltip" />
        </div>
        {/* Spinner while ad is being created/updated */}
        {loading && <Loading text="Skapar annons..." />}
        {showErrorModal && (
          <ConfirmationModal
            heading="Någonting gick fel..."
            text={
              errorMessage ||
              "Ett problem uppstod med skapandet av din annons. Vänligen prova igen senare."
            }
            setShowModal={setShowErrorModal}
            error
          />
        )}
        {showSaveAdModal && (
          <ConfirmationModal
            heading="Din annons är sparad som inaktiv"
            text="Gå till inaktiva annoner under Mina annonser för att aktivera din annons eller för att fortsätta redigera den."
            linkTo="/mina-sidor/annonser"
            setShowModal={setShowSaveAdModal}
            note={errorMessage ? errorMessage : ""}
          />
        )}
        {showCreateAdModal && (
          <ConfirmationModal
            heading="Din annons är nu skapad!"
            text="Grattis! Din annons är nu aktiv och vi kontakter dig så fort någon är intresserad av att hyra din lokal. För att hanterar dina annonser så gå du till Mina annonser."
            linkTo="/mina-sidor/annonser"
            linkToAd={
              newAdId
                ? `/lediga-lokaler/${encodeURIComponent(
                    formData.municipality.toLowerCase(),
                  )}/${encodeURIComponent(
                    formData.type_of_room.toLowerCase(),
                  )}/${encodeURIComponent(
                    formData.address.toLowerCase(),
                  )}/${newAdId}`
                : ""
            }
            setShowModal={setShowCreateAdModal}
            note={errorMessage ? errorMessage : ""}
          />
        )}
        {showWarningModal && (
          <WarningModal
            heading="Lämna utan att spara"
            text="Är du säker att du vill lämna sidan? Din annons är ej sparad."
            confirmBtnText="Lämna"
            onCancel={cancelNavigation}
            onConfirm={() => {
              setAdPreviewInfo({ info: {}, images: [], hasChanged: false });
              confirmNavigation();
            }}
          />
        )}
      </>
    )
  );
}

export default CreateAd;
